import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import type {
  DashboardTopProductsCardProps,
  MoneyUnitStyle,
} from "@/modules/dashboard/types/dashboard-analytics.types";
import { MoneyValue } from "@/modules/dashboard/components/redesign/MoneyValue";
import { formatCurrencyParts } from "@/lib/formatting/currency";
import { intlLocale } from "@/lib/formatting/locale";

// Product · Units sold · Revenue · Profit. Four aggregate columns need far less
// room than the six-column product table, but the breakpoint matches
// DashboardProductListCard's so every dashboard list flips layout on the same
// line — below `xl` rows collapse to two lines rather than scrolling sideways.
/** Body-row tier: these figures sit at body size, so the unit tracks them. */
const ROW_UNIT_STYLE: MoneyUnitStyle = {
  className: "text-[11px] font-semibold text-[color:var(--text-muted)]",
  gap: "4px",
};

const GRID =
  "[grid-template-columns:minmax(0,2.6fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)]";

/** Rank chip standing in for the product thumbnail — these analytics rows
 * carry no image, and borrowing one from a separate products fetch would tie
 * this list to a catalog page it no longer depends on. */
function RankBadge({ rank }: { rank: number }): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--line)] bg-[color:var(--soft)] font-mono text-[12px] font-bold text-[color:var(--text-muted)]"
    >
      {rank}
    </span>
  );
}

function Money({
  value,
  locale,
  strong = false,
}: {
  locale: string;
  value: number;
  strong?: boolean;
}): ReactElement {
  // Same helper, same tier styles, same side as every other money figure on the
  // dashboard. This used to call `toLocaleString()` with no locale — which
  // always emits Latin digits — and render the symbol as a plain 11px/400 span
  // on the opposite side from `MoneyValue`, so one card disagreed with the rest
  // of the page on all three counts.
  const parts = formatCurrencyParts(value, locale);
  return (
    <span
      className={
        strong
          ? "inline-flex min-w-0 items-baseline truncate font-bold text-[color:var(--text-primary)]"
          : "inline-flex min-w-0 items-baseline truncate text-[color:var(--text-secondary)]"
      }
    >
      <MoneyValue {...parts} unitStyle={ROW_UNIT_STYLE} />
    </span>
  );
}

/**
 * Top products for the selected range, driven by the backend's SQL aggregate.
 *
 * These rows are `SellerDashboardTopProduct` (units / revenue / profit), NOT
 * `ProductView` — there is no status, stock or thumbnail to show — so this
 * cannot reuse `DashboardProductListCard`. It deliberately copies that card's
 * shell, `--soft` header strip, hairline dividers, hover tint and below-`xl`
 * two-line collapse so the dashboard's list sections still read as one system.
 *
 * Direction: only logical utilities (`ms-auto`, `pe-*`, `text-end`), so the
 * layout mirrors correctly under RTL.
 */
export function DashboardTopProductsCard({
  title,
  subtitle,
  emptyLabel,
  products,
  locale,
  productHref,
}: DashboardTopProductsCardProps): ReactElement {
  const t = useTranslations();

  return (
    <div className="overflow-hidden rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)]">
      <div className="flex items-center justify-between gap-3 px-[18px] pb-[13px] pt-4">
        <div className="min-w-0">
          <div className="text-[16px] font-bold text-[color:var(--text-primary)]">
            {title}
          </div>
          <div className="mt-px text-[12.5px] text-[color:var(--text-muted)]">
            {subtitle}
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="border-t border-[color:var(--line)] px-[18px] py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {emptyLabel}
        </div>
      ) : (
        <div>
          {/* Column headers only make sense next to the column layout. */}
          <div
            className={`hidden ${GRID} border-y border-[color:var(--line)] bg-[color:var(--soft)] px-[18px] py-[11px] text-[12px] font-semibold text-[color:var(--text-muted)] xl:grid`}
          >
            <span>{t(keys.products.tableColProduct)}</span>
            <span>{t(keys.dashboard.tableUnitsSold)}</span>
            <span>{t(keys.dashboard.tableRevenue)}</span>
            <span className="text-end">{t(keys.dashboard.tableProfit)}</span>
          </div>

          <div className="border-t border-[color:var(--line)] xl:border-t-0">
            {products.map((product, index) => {
              const name =
                pickLocalized(locale, product.nameAr, product.nameEn) ||
                product.nameEn ||
                product.nameAr;
              const unitsText = t(keys.dashboard.unitsSoldCount, {
                count: product.unitsSold,
              });

              return (
                <Link
                  key={product.productId}
                  to={productHref(product.productId)}
                  className="block border-b border-[color:var(--hair)] px-[18px] transition-colors last:border-b-0 hover:bg-[color:var(--soft)]"
                >
                  {/* Column layout (xl+) */}
                  <div
                    className={`hidden ${GRID} items-center py-[11px] text-[12.5px] xl:grid`}
                  >
                    <span className="flex min-w-0 items-center gap-[11px] pe-3 font-semibold">
                      <RankBadge rank={index + 1} />
                      <span className="truncate text-[color:var(--text-primary)]">
                        {name}
                      </span>
                    </span>
                    <span className="truncate pe-3 text-[color:var(--text-secondary)]">
                      <bdi>{product.unitsSold.toLocaleString(intlLocale(locale))}</bdi>
                    </span>
                    <span className="min-w-0 pe-3">
                      <Money locale={locale} value={product.revenue} />
                    </span>
                    <span className="flex min-w-0 items-center justify-end">
                      <Money
                        locale={locale}
                        value={product.profit}
                        strong
                      />
                    </span>
                  </div>

                  {/* Two-line layout (below xl) — no horizontal scrolling. */}
                  <div className="flex flex-col gap-2 py-3 xl:hidden">
                    <div className="flex items-center gap-2.5">
                      <RankBadge rank={index + 1} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold leading-[1.3] text-[color:var(--text-primary)]">
                          {name}
                        </span>
                        <span className="block truncate text-[11.5px] text-[color:var(--text-muted)]">
                          {unitsText}
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
                      <span className="inline-flex min-w-0 items-baseline gap-1.5">
                        <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">
                          {t(keys.dashboard.tableRevenue)}
                        </span>
                        <Money locale={locale} value={product.revenue} />
                      </span>
                      <span className="ms-auto inline-flex min-w-0 items-baseline gap-1.5">
                        <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">
                          {t(keys.dashboard.tableProfit)}
                        </span>
                        <Money
                          locale={locale}
                          value={product.profit}
                          strong
                        />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
